import React, {useState} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';

import customerPath from 'utils/customerPath';
import FontAwesome from 'utils/FA';
import { delayedJobs, modals } from 'ducks/index';
import { momentFormat } from './helpers';

import DelayedJobDetails from './DelayedJobDetails';
import PartialErrorBoundary from '../shared/PartialErrorBoundary';
import FA from 'utils/FA';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle';

const BrokenJob = () => <tr>
  <td colSpan={8}>
    <div className={'alert alert-danger'}>
      <FA icon={faExclamationTriangle} nbsp/>
      THIS JOB IS VERY BROKEN.  Please see JS console errors!
    </div>
  </td>
</tr>;

/**
 * A single delayed job row
 */
const DelayedJob = (
  {
    job,
    deleteJob,
    replayJob,
    job: {
      created_at: createdAt, customer, last_error: lastError, priority, status, job_class, type, run_at: runAt
    }
  }
) => {
  const [showDetails, setShowDetails] = useState(false);

  return <PartialErrorBoundary errorContent={BrokenJob}>
    <tr className={`delayed-job ${lastError !== null ? 'delayed-job-failed' : ''}`}>
      <td><FontAwesome name='circle' className={`delayed-job-status-${status}`}/></td>
      <td>{priority}</td>
      <td>{moment(createdAt).calendar(null, momentFormat)}</td>
      <td>{moment(runAt).calendar(null, momentFormat)}</td>
      <td>{job_class}</td>
      <td>{type}</td>
      <td>
        {
          customerPath(customer)
            ? <a href={customerPath(customer)}>{customer}</a>
            : customer
        }
      </td>
      <td>
        <button className='btn btn-xs btn-light' type='button' onClick={() => setShowDetails(!showDetails)}>
          <FontAwesome name='info-circle' className='delayed-job-button-icon'/>
          Details
        </button>
        &nbsp;
        {
          lastError !== null && (
            <React.Fragment>
              <button className='btn btn-xs btn-primary'
                      type='button'
                      onClick={() => replayJob(job)}>
                <FontAwesome name='repeat' className='delayed-job-button-icon'/>
                Replay
              </button>
              &nbsp;
              <button className='btn btn-xs btn-danger'
                      type='button'
                      onClick={() => deleteJob(job)}>
                <FontAwesome name='times-circle' className='delayed-job-button-icon'/>
                Delete
              </button>
            </React.Fragment>
          )
        }
      </td>
    </tr>
    {showDetails &&
      <tr>
        <td colspan="8">
          <DelayedJobDetails job={job}/>
        </td>
      </tr>
    }
  </PartialErrorBoundary>
};

DelayedJob.propTypes = {
  /** A delayed job */
  job: PropTypes.shape({
    /** Creation date */
    created_at: PropTypes.string,

    /** Customer name for the job, if available: */
    customer: PropTypes.string,

    /** The last error encountered */
    last_error: PropTypes.string,

    /** Job priority */
    priority: PropTypes.number,

    /** Job status: */
    status: PropTypes.string,

    /** Delayed job type (the method that's going to be run by the job) */
    type: PropTypes.string,

    /** The job class (wrapper used to pass the job into DJ */
    job_class: PropTypes.string
  }),

  /** Deletes a delayed job from the queue */
  deleteJob: PropTypes.func,

  /** Runs a failed job immediately **/
  replayJob: PropTypes.func,

  /** Opens a modal that shows delayed job details */
  showDelayedJob: PropTypes.func
};

DelayedJob.defaultProps = {
  job: {},
  deleteJob: () => false,
  replayJob: () => false,
  showDelayedJob: () => false
};

const ConnectedDelayedJob = connect(
  () => ({}),
  (dispatch) => ({
    showDelayedJob: (job) => dispatch(modals.actions.show('DelayedJobModal', {...job})),
    deleteJob: (job) => dispatch(delayedJobs.actions.remove(job))
      .then(dispatch(delayedJobs.actions.load())),
    replayJob: (job) =>
      dispatch(delayedJobs.actions.update({
        ...job,
        run_at: moment().toISOString(),
        last_error: null
      }))
        .then(dispatch(delayedJobs.actions.load()))
        .then(() => Utility.displayFlashMessages({
          notice: 'Job has been requeued and will run as soon as a worker is available'
        }))
  })
)(DelayedJob);

export {
  DelayedJob,
  ConnectedDelayedJob as default
};
